import { MutationKey, useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import {
  Abi,
  AbiStateMutability,
  ContractFunctionArgs,
  ContractFunctionName,
  ContractFunctionParameters,
  encodeFunctionData,
  TransactionReceipt,
} from 'viem'
import {
  UseSimulateContractParameters,
  UseWriteContractParameters,
  UseWriteContractReturnType,
  useConfig,
  useSimulateContract,
} from 'wagmi'
import { writeContractMutationOptions } from 'wagmi/query'

import { useIncreasedGasLimit } from './useIncreasedGasLimit'
import { useWaitForTransactionReceiptUniversal } from './useWaitForTransactionReceiptUniversal'
import { HandledWriteError } from '@/domain/errors/HandledWriteError'
import { PreWriteCheckError } from '@/domain/errors/PreWriteCheckError'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { __TX_LIST_KEY } from '@/test/e2e/constants'
import { JSONStringifyRich } from '@/utils/object'
import { captureError } from '@/utils/sentry'

export type WriteStatus =
  | { kind: 'disabled' }
  | { kind: 'simulating' }
  | { kind: 'ready' }
  | { kind: 'tx-sending' }
  | { kind: 'tx-mining' }
  | { kind: 'success' }
  | { kind: 'error'; errorKind: WriteErrorKind; error: Error }
export type WriteErrorKind = 'simulation' | 'before-write' | 'tx-submission' | 'tx-reverted'

export interface UseWriteResult {
  write: () => void
  resimulate: () => void
  reset: () => void
  status: WriteStatus
}

export interface UseWriteOptions {
  onTransactionSettled?: {
    mounted?: (txReceipt: TransactionReceipt) => void
    unmounted?: () => void
  }
  onBeforeWrite?: () => void // @note: good place to run extra sanity checks
  mutationKeyPrefix?: MutationKey
}

/**
 * Write to a contract with a sane API.
 * - Prepares txs
 * - Waits for tx to be mined
 * - Does not propagate simulation errors if disabled
 */
export function useWrite<TAbi extends Abi, TFunctionName extends ContractFunctionName<TAbi, 'nonpayable' | 'payable'>>(
  args: UseSimulateContractParameters<TAbi, TFunctionName> & { enabled?: boolean },
  options: UseWriteOptions = {},
): UseWriteResult {
  const enabled = args.enabled ?? true

  const { account } = useConnectedAddress({ throwOnNotConnected: false })

  const {
    data: gasLimit,
    isLoading: isGasLimitLoading,
    isReady: isGasLimitReady,
  } = useIncreasedGasLimit({ ...args } as any)
  const {
    data: parameters,
    error: _simulationError,
    refetch: resimulate,
    isLoading: isSimulationLoading,
  } = useSimulateContract({
    account,
    gas: gasLimit,
    ...args,
    query: {
      gcTime: 0,
      enabled: enabled && isGasLimitReady,
      ...args.query,
    },
  } as any)
  // @note: workaround for wagmi serving results from cache even if enabled = false. https://github.com/wevm/wagmi/issues/888
  const simulationError = enabled ? _simulationError : undefined

  const {
    writeContract,
    data: txHash,
    isPending: isTxSending,
    isSuccess: wasTxSent,
    error: _txSubmissionError,
    reset,
  } = useWriteContract({
    mutationKey: [...(options.mutationKeyPrefix ?? []), ...getWriteContractMutationKey(args as any)],
    onBeforeWrite: options.onBeforeWrite,
  })

  const {
    data: txReceipt,
    error: txReceiptError,
    isLoading: isTxReceiptLoading,
  } = useWaitForTransactionReceiptUniversal(
    {
      type: 'regular',
      hash: txHash,
    },
    {
      onSettledUnmounted: options.onTransactionSettled?.unmounted,
    },
  )
  const txSubmissionError = enabled ? _txSubmissionError : undefined

  // biome-ignore lint(useExhaustiveDependencies): callback should only be called when txReceipt changes
  useEffect(() => {
    if (txReceipt) {
      options.onTransactionSettled?.mounted?.(txReceipt)
      if (import.meta.env.VITE_PLAYWRIGHT === '1') {
        // @note: for e2e tests needs we store sent transactions
        storeRequest(parameters?.request)
      }
    }
  }, [txReceipt])

  const status = ((): WriteStatus => {
    if (wasTxSent && txReceipt) {
      // txReceipt is only available when tx didn't revert
      return { kind: 'success' }
    }
    if (isTxSending) {
      return { kind: 'tx-sending' }
    }
    if (isTxReceiptLoading) {
      return { kind: 'tx-mining' }
    }

    // enabled flag doesn't allow to trigger the write, but if it's already sent it doesn't affect the status
    if (!enabled) {
      return { kind: 'disabled' }
    }
    if (isSimulationLoading || isGasLimitLoading) {
      return { kind: 'simulating' }
    }
    if (simulationError) {
      return { kind: 'error', errorKind: 'simulation', error: simulationError }
    }
    if (txSubmissionError) {
      if (txSubmissionError instanceof PreWriteCheckError) {
        return { kind: 'error', errorKind: 'before-write', error: txSubmissionError }
      }
      return { kind: 'error', errorKind: 'tx-submission', error: txSubmissionError }
    }
    if (wasTxSent && txReceiptError) {
      return { kind: 'error', errorKind: 'tx-reverted', error: txReceiptError }
    }

    return { kind: 'ready' }
  })()

  const error = status.kind === 'error' ? status.error : undefined
  useEffect(() => {
    if (error) {
      captureError(new HandledWriteError(error))
    }
  }, [error])

  const finalWrite =
    parameters && enabled
      ? () => {
          writeContract(parameters.request as any)
        }
      : () => {}

  return {
    reset,
    write: finalWrite,
    resimulate,
    status,
  }
}

// useful for creating conditional configs without losing type-safety
export function ensureConfigTypes<
  abi extends Abi | readonly unknown[] = Abi,
  mutability extends AbiStateMutability = AbiStateMutability,
  functionName extends ContractFunctionName<abi, mutability> = ContractFunctionName<abi, mutability>,
  args extends ContractFunctionArgs<abi, mutability, functionName> = ContractFunctionArgs<
    abi,
    mutability,
    functionName
  >,
>(
  config: ContractFunctionParameters<abi, mutability, functionName, args> & {
    value?: bigint
  },
): typeof config {
  return config as any
}

function storeRequest(request: any): void {
  if (!request) {
    return
  }
  const txList: any[] = Array.isArray(window[__TX_LIST_KEY]) ? (window[__TX_LIST_KEY] as any) : []
  const calldata = encodeFunctionData(request as any)
  txList.push({ ...request, calldata })
  window[__TX_LIST_KEY] = txList as any
}

// @note: This function is a copy of Wagmi's useWriteContract with an option to pass a mutationKey.
// Passing custom mutationKey can be useful for resetting/invalidating mutation.
function useWriteContract(
  parameters: UseWriteContractParameters & { mutationKey?: MutationKey; onBeforeWrite?: () => void } = {},
): UseWriteContractReturnType {
  const { mutation, mutationKey, onBeforeWrite } = parameters

  const config = useConfig(parameters)

  const mutationOptions = writeContractMutationOptions(config)
  const { mutate, mutateAsync, ...result } = useMutation({
    ...mutation,
    ...mutationOptions,
    mutationKey,
    onMutate: async (...args) => {
      try {
        onBeforeWrite?.()
      } catch (error: unknown) {
        throw new PreWriteCheckError(error)
      }

      return mutation?.onMutate?.(...args)
    },
  })

  type Return = UseWriteContractReturnType
  return {
    ...result,
    writeContract: mutate as Return['writeContract'],
    writeContractAsync: mutateAsync as Return['writeContractAsync'],
  }
}

function getWriteContractMutationKey(args: UseSimulateContractParameters<Abi, string>): MutationKey {
  return [args.chainId, args.address, args.functionName, JSONStringifyRich(args.args)]
}
